import React, { useCallback } from 'react';
import { Text, View, TouchableOpacity } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import {
  DocketDisplayOtherItem,
  DocketDisplayNotifierHeaderItem,
  DocketDetailsProps,
} from '../../../common/types';
import DocketItemStyles from '../DocketItem.styles';
import {
  bulkSelectVar,
  selectedDocketVar,
  toggleNotifierSection,
} from '../../../state/apolloVars';
import Icon from '../../Icon/Icon';
import { useColors } from '../../../hooks/useColors';
import { useNotifierDisplayStyle } from '../../../hooks/useNotifierDisplayStyle';

function OtherItemsDisplay({
  docketItem,
  onToggleDocketDetails,
}: {
  docketItem: DocketDisplayOtherItem;
  onToggleDocketDetails: (props: DocketDetailsProps) => void;
}) {
  const { translate } = useTranslation();
  const styles = DocketItemStyles();
  const notifierItems = docketItem.notifierItems;
  const isBulkMode = bulkSelectVar();
  const isSelected = Object.keys(selectedDocketVar())?.length > 0;
  const { colors } = useColors();
  const displayStyle = useNotifierDisplayStyle(docketItem.printerProfileId);

  // Create the callback at the top level, before any conditional returns
  const handlePress = useCallback(() => {
    // Only execute the logic if otherItems exists
    onToggleDocketDetails({
      docketId: docketItem.docketId,
      items: docketItem.items,
      notifierItems: notifierItems,
      orderNumber: docketItem.orderNumber || '',
      status: docketItem.status,
      orderTypeId: docketItem.orderTypeId,
      allergens: docketItem.allergens,
      displayStyle,
    });
  }, [docketItem, notifierItems, onToggleDocketDetails, displayStyle]);

  // Add safety checks
  if (
    !notifierItems ||
    !notifierItems.length ||
    !notifierItems[0]?.items?.length
  ) {
    return null;
  }

  const totalItemQuantity = notifierItems.reduce((total, item) => {
    if (item.items) {
      return (
        total +
        item.items.reduce((itemTotal, subItem) => {
          return itemTotal + (subItem.quantity || 0);
        }, 0)
      );
    }
    return 0;
  }, 0);

  return (
    <TouchableOpacity onPress={handlePress} disabled={isBulkMode || isSelected}>
      <View style={styles.otherItemsContainer}>
        <Text style={styles.otherItemsText}>
          {totalItemQuantity > 1
            ? `${translate('kitchenDisplay.otherItems')} (${totalItemQuantity})`
            : `${translate('kitchenDisplay.otherItem')} (${totalItemQuantity})`}
        </Text>
        <Icon color={colors.text} name={'angle-right'} />
      </View>
    </TouchableOpacity>
  );
}

function NotifierSectionHeader({
  header,
}: {
  header: DocketDisplayNotifierHeaderItem;
}) {
  const { translate } = useTranslation();
  const styles = DocketItemStyles();
  const { colors } = useColors();
  const isBulkMode = bulkSelectVar();
  const isSelected = Object.keys(selectedDocketVar())?.length > 0;
  const { docketId, totalQuantity, isCollapsed } = header;

  return (
    <TouchableOpacity
      testID="notifier-header"
      accessibilityRole="button"
      accessibilityState={{ expanded: !isCollapsed }}
      onPress={() => toggleNotifierSection(docketId)}
      disabled={isBulkMode || isSelected}
    >
      <View style={styles.otherItemsContainer}>
        <Text style={styles.otherItemsText}>
          {totalQuantity > 1
            ? `${translate('kitchenDisplay.otherItems')} (${totalQuantity})`
            : `${translate('kitchenDisplay.otherItem')} (${totalQuantity})`}
        </Text>
        <Icon
          color={colors.text}
          name={isCollapsed ? 'angle-right' : 'angle-down'}
        />
      </View>
    </TouchableOpacity>
  );
}

export default function DocketOtherItemsLabel({
  docketItem,
  onToggleDocketDetails,
  notifierHeader,
}: {
  docketItem?: DocketDisplayOtherItem;
  onToggleDocketDetails: (props: DocketDetailsProps) => void;
  notifierHeader?: DocketDisplayNotifierHeaderItem;
}) {
  if (notifierHeader) return <NotifierSectionHeader header={notifierHeader} />;
  if (!docketItem) return null;
  return (
    <OtherItemsDisplay
      docketItem={docketItem}
      onToggleDocketDetails={onToggleDocketDetails}
    />
  );
}
